import { useSelector } from 'react-redux';
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import Product from '../components/Product';
import Loader from '../components/Loader';
import Message from '../components/Message';
import { useGetRandomCategoryProductsQuery } from '../slices/productsApiSlice.js';
import { useGetExchangeRatesQuery } from '../slices/exchangeSlice.js';
import { formatPrice } from '../utils/formatPrice.js';
import BannerAd from "../components/BannerAd";
import { banners } from "../constants.js";
import { getRecentlySeenProducts } from "../utils/recentlySeen.js";

const capitalizeWords = (str) =>
  str.replace(/\b\w/g, (char) => char.toUpperCase());

const HomeScreen = () => {
  const { data, isLoading, error } = useGetRandomCategoryProductsQuery({ pageNumber: 1 });
  const { data: exchangeData } = useGetExchangeRatesQuery();
  const [recentlySeenPage, setRecentlySeenPage] = useState(0);

  const userInfo = useSelector((state) => state.auth.userInfo);
  const preferredCurrency = userInfo?.preferredCurrency || 'USD';
  const recentlySeenProducts = useMemo(
    () => getRecentlySeenProducts(userInfo?._id),
    [userInfo?._id]
  );
  const recentlySeenPages = Math.max(1, Math.ceil(recentlySeenProducts.length / 4));
  const recentlySeenSlice = recentlySeenProducts.slice(recentlySeenPage * 4, recentlySeenPage * 4 + 4);

  useEffect(() => {
    if (recentlySeenPage > recentlySeenPages - 1) {
      setRecentlySeenPage(0);
    }
  }, [recentlySeenPage, recentlySeenPages]);

  if (isLoading) return <p><Loader /></p>;
  if (error) return <Message variant="danger">Error loading products</Message>;

  const categoriesArray = Object.entries(data);
  const homepageCategories = categoriesArray.slice(0, 3);

  return (
    <div className="space-y-10 pb-8">
      <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-sky-50 via-white to-emerald-50 p-6 shadow-sm md:p-10">
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-sky-200/40 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 left-1/4 h-56 w-56 rounded-full bg-emerald-200/40 blur-3xl" />
        <div className="relative z-10">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">
            ProShare Marketplace
          </p>
          <h1 className="max-w-3xl text-3xl font-semibold leading-tight text-slate-900 md:text-4xl">
            Quality resources for allied health professionals.
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-slate-600 md:text-base">
            Explore high-quality materials, practical worksheets, and ready-to-use bundles created
            to support real sessions, real planning, and real progress. ProShare helps you spend
            less time building resources from scratch and more time delivering confident, effective
            work. Prices display in <strong>{preferredCurrency}</strong> when available.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/register"
              className="inline-flex items-center rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white no-underline shadow-sm transition hover:bg-sky-700"
            >
              Join ProShare Free
            </Link>
            <Link
              to="/search"
              className="inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 no-underline shadow-sm transition hover:bg-slate-50"
            >
              Browse Ready-to-Use Resources
            </Link>
          </div>
        </div>
      </section>

      <section className="p-1 md:p-2">
        <div className="mb-3 text-sm font-medium text-slate-700">Quick Browse</div>
        <div className="flex flex-wrap gap-2">
          {homepageCategories.map(([categoryName, categoryData]) => (
            <span
              key={categoryName}
              className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-700 shadow-sm"
            >
              {capitalizeWords(categoryName)} ({categoryData?.count || 0})
            </span>
          ))}
        </div>
      </section>

      {homepageCategories.map(([categoryName, categoryData], index) => (
        <section
          key={categoryName}
          className="p-1 md:p-2"
        >
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3 border-b border-slate-200/70 pb-3">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">
                {capitalizeWords(categoryName)} Resources
              </h2>
              <p className="text-sm text-slate-600">
                Showing {categoryData.products.length} of {categoryData.count} products in this category group.
              </p>
            </div>
            <Link
              to={`/search/${encodeURIComponent(categoryName)}`}
              className="inline-flex items-center rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 no-underline shadow-sm hover:bg-slate-50"
            >
              See more
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {categoryData.products.map((product) => {
              let displayPrice = product.price;
              if (
                preferredCurrency !== 'USD' &&
                exchangeData?.rates?.[preferredCurrency]
              ) {
                displayPrice = formatPrice(product.price, preferredCurrency, exchangeData);
              }

              return (
                <div key={product._id}>
                  <Product
                    product={product}
                    displayPrice={displayPrice}
                    currency={preferredCurrency}
                    className=""
                  />
                </div>
              );
            })}
          </div>

          {index < 3 && banners.length > 0 && (
            <div className="mt-6">
              <BannerAd banner={banners[index % banners.length]} />
            </div>
          )}
        </section>
      ))}

      {recentlySeenProducts.length > 0 && (
        <section className="p-1 md:p-2">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3 border-b border-slate-200/70 pb-3">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Recently Seen</h2>
              <p className="text-sm text-slate-600">
                Last {recentlySeenProducts.length} product
                {recentlySeenProducts.length === 1 ? "" : "s"} you viewed
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {recentlySeenSlice.map((product) => {
              let displayPrice = product.price;
              if (
                preferredCurrency !== "USD" &&
                exchangeData?.rates?.[preferredCurrency]
              ) {
                displayPrice = formatPrice(product.price, preferredCurrency, exchangeData);
              }

              return (
                <div key={`recent-${product._id}`}>
                  <Product
                    product={product}
                    displayPrice={displayPrice}
                    currency={preferredCurrency}
                  />
                </div>
              );
            })}
          </div>

          {recentlySeenPages > 1 && (
            <div className="mt-4 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setRecentlySeenPage((prev) => Math.max(0, prev - 1))}
                disabled={recentlySeenPage === 0}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Previous recently seen page"
              >
                <FaChevronLeft />
              </button>
              <span className="text-sm font-medium text-slate-600">
                {recentlySeenPage + 1} / {recentlySeenPages}
              </span>
              <button
                type="button"
                onClick={() =>
                  setRecentlySeenPage((prev) =>
                    Math.min(recentlySeenPages - 1, prev + 1)
                  )
                }
                disabled={recentlySeenPage >= recentlySeenPages - 1}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Next recently seen page"
              >
                <FaChevronRight />
              </button>
            </div>
          )}
        </section>
      )}

      <section className="rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-sm md:p-8">
        <h2 className="text-2xl font-semibold text-slate-900 md:text-3xl">
          Ready to build your library?
        </h2>
        <p className="mx-auto mt-2 max-w-2xl text-sm text-slate-600 md:text-base">
          Create your account to discover standout resources, save your favorites, follow creators,
          and keep practical bundles within reach whenever you need them. It is a simpler way to
          find quality tools, stay organized, and keep great materials close to your daily work.
        </p>
        <div className="mt-5">
          <Link
            to="/register"
            className="inline-flex items-center rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white no-underline shadow-sm transition hover:bg-sky-700"
          >
            Start Building Your Library
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomeScreen;
